// ─────────────────────────────────────────────────────────────────────────────
// Prints do incidente.
//
// O print é o que faz o acervo servir: quem procura um erro conhecido quer ver
// a tela, não ler a descrição dela. Mas print é imagem grande, e imagem grande
// em base64 estoura o payload do webhook e o PDF.
//
// Por isso três limites, todos herdados do 1.4.1:
//
//   · no máximo 8 prints por incidente — acima disso é documentação, não erro;
//   · 5 MB por imagem;
//   · acima de 1600px no maior lado, a imagem é REDUZIDA e reconvertida em
//     JPEG 0,82. Um print de monitor 4K tem 8 MB e nenhuma informação a mais
//     que a mesma tela em 1600px.
//
// O que continuar grande depois da compressão é recusado com o motivo — some
// da lista, mas com aviso, nunca em silêncio.
// ─────────────────────────────────────────────────────────────────────────────

export const MAX_PRINTS = 8;
export const MAX_BYTES = 5 * 1024 * 1024;
/** Depois de comprimir, ainda aceitamos uma folga: comprimir nem sempre encolhe. */
export const FOLGA_POS_COMPRESSAO = 1.4;
export const LADO_MAXIMO = 1600;
export const QUALIDADE_JPEG = 0.82;

/** Lê um File como data URL. */
function lerComoDataUrl(file) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = ev => resolve(ev.target.result);
    leitor.onerror = () => reject(new Error('não consegui ler o arquivo'));
    leitor.readAsDataURL(file);
  });
}

/** Carrega um data URL num <img> para saber as dimensões e poder desenhar. */
function carregarImagem(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('arquivo não é uma imagem válida'));
    img.src = dataUrl;
  });
}

/** Quantos bytes um data URL representa (o base64 infla ~4/3). */
export function bytesDoDataUrl(dataUrl) {
  const base64 = String(dataUrl || '').split(',')[1] || '';
  return Math.round((base64.length * 3) / 4);
}

/**
 * Reduz a imagem se ela passar de LADO_MAXIMO, reconvertendo em JPEG.
 * Imagem pequena volta como veio — recomprimir só perderia qualidade.
 */
export async function reduzirSePreciso(dataUrl) {
  const img = await carregarImagem(dataUrl);
  const maior = Math.max(img.width, img.height);
  if (maior <= LADO_MAXIMO) return { dataUrl, largura: img.width, altura: img.height, reduzida: false };

  const escala = LADO_MAXIMO / maior;
  const largura = Math.round(img.width * escala);
  const altura = Math.round(img.height * escala);

  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext('2d');
  // Fundo branco: JPEG não tem transparência, e sem isto o que era transparente
  // vira preto — um print com cantos arredondados fica com moldura escura.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, largura, altura);
  ctx.drawImage(img, 0, 0, largura, altura);

  return {
    dataUrl: canvas.toDataURL('image/jpeg', QUALIDADE_JPEG),
    largura,
    altura,
    reduzida: true,
  };
}

/**
 * Transforma arquivos escolhidos em prints do incidente.
 *
 * @returns {Promise<{prints: Array, recusados: Array<{nome, motivo}>}>}
 */
export async function prepararPrints(arquivos, jaTem = 0) {
  const prints = [];
  const recusados = [];
  let cabem = Math.max(0, MAX_PRINTS - jaTem);

  for (const file of Array.from(arquivos || [])) {
    if (!cabem) {
      recusados.push({ nome: file.name, motivo: `limite de ${MAX_PRINTS} prints` });
      continue;
    }
    if (!String(file.type || '').startsWith('image/')) {
      recusados.push({ nome: file.name, motivo: 'não é imagem' });
      continue;
    }
    if (file.size > MAX_BYTES * FOLGA_POS_COMPRESSAO) {
      recusados.push({ nome: file.name, motivo: 'maior que 7 MB' });
      continue;
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      const bruto = await lerComoDataUrl(file);
      // eslint-disable-next-line no-await-in-loop
      const { dataUrl, largura, altura, reduzida } = await reduzirSePreciso(bruto);

      const bytes = bytesDoDataUrl(dataUrl);
      if (bytes > MAX_BYTES * FOLGA_POS_COMPRESSAO) {
        recusados.push({ nome: file.name, motivo: 'continuou grande depois de comprimir' });
        continue;
      }

      prints.push({
        data_url: dataUrl,
        mime: reduzida ? 'image/jpeg' : (file.type || 'image/png'),
        tamanho: bytes,
        nome: file.name || 'print.png',
        legenda: '',
        largura,
        altura,
      });
      cabem -= 1;
    } catch (e) {
      recusados.push({ nome: file.name, motivo: e.message || 'falha ao ler' });
    }
  }

  return { prints, recusados };
}

/**
 * Converte as imagens do ATENDIMENTO em prints do incidente.
 *
 * O helpdesk guarda `{ id, dataUrl, base64, nome, legenda }`; o incidente usa
 * `{ data_url, mime, tamanho, nome, legenda }`. Sem esta ponte, quem registrava
 * um incidente a partir de um atendimento tinha de anexar tudo de novo — e
 * geralmente não anexava.
 *
 * Passa pelo mesmo redutor de quem anexa à mão: um print de 4K colado na
 * formalização não pode entrar no incidente do jeito que veio.
 */
export async function printsDoAtendimento(imagens = [], jaTem = 0) {
  const prints = [];
  const recusados = [];
  let cabem = Math.max(0, MAX_PRINTS - jaTem);

  for (const img of imagens) {
    if (!img?.dataUrl) continue;
    if (!cabem) {
      recusados.push({ nome: img.nome || 'imagem', motivo: `limite de ${MAX_PRINTS} prints` });
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const { dataUrl, largura, altura, reduzida } = await reduzirSePreciso(img.dataUrl);
      const bytes = bytesDoDataUrl(dataUrl);
      if (bytes > MAX_BYTES * FOLGA_POS_COMPRESSAO) {
        recusados.push({ nome: img.nome || 'imagem', motivo: 'acima do limite' });
        continue;
      }
      prints.push({
        data_url: dataUrl,
        mime: reduzida ? 'image/jpeg' : 'image/png',
        tamanho: bytes,
        nome: img.nome || 'print.png',
        // A legenda escrita na formalização vale para o incidente também.
        legenda: img.legenda || '',
        largura,
        altura,
      });
      cabem -= 1;
    } catch (e) {
      recusados.push({ nome: img.nome || 'imagem', motivo: e.message || 'falha ao converter' });
    }
  }

  return { prints, recusados };
}

/** Resumo do que entrou e do que ficou de fora, para uma linha de aviso. */
export function resumoDaMigracao({ prints, recusados }) {
  if (!prints.length && !recusados.length) return 'Nenhum print no atendimento para anexar';
  const partes = [`${prints.length} print(s) do atendimento anexado(s)`];
  if (recusados.length) partes.push(`${recusados.length} fora do limite`);
  return partes.join(' — ');
}
